import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { accountTableFields } from '../../constants/accountTable';
import { DeleteOutlined } from '@ant-design/icons';
import {
  Alert,
  Table,
  InputNumber,
  Button,
  message,
  Popconfirm,
  notification,
} from 'antd';
import _ from 'lodash';
import { withTranslation } from 'react-i18next';
import { NAMESPACES, tCommon } from '../../i18n';
import { TableRowActions, TableActions } from '../';
import { goBack } from '../../utils/gotoLink';
import {
  translateBiofuelTypeClassificator,
  translateBooleanClassificator,
  translateCleaningTechnologyClassificator,
  translateFuelTypeClassificator,
  translateHydrogenFuelTypeClassificator,
  translateHydrogenTechnologyTypeClassificator,
  translateProductionPathwayClassificator,
  translateProductionTechnologyClassificator,
  translateCountryClassificator,
} from '../../utils/translateHelpers';
import gasType from '../../constants/classificators/gasType';
import ExpandedRowRenderer from './ExpandedRowRenderer';
import dayjs from 'dayjs';
import classifiersStore from '../../utils/classifiersStore';

class PerformCertificatesTable extends Component {
  static propTypes = {
    dataList: PropTypes.array,
    t: PropTypes.func.isRequired,
    performAction: PropTypes.func.isRequired,
    validateAction: PropTypes.func,
    goAmountTotal: PropTypes.number,
    isCanceling: PropTypes.bool,
    isExporting: PropTypes.bool,
    isCancelAvailable: PropTypes.bool,
    isUsersOtherAccount: PropTypes.bool,
    isTransportPoint: PropTypes.bool,
    eecsGoRows: PropTypes.array,
  };

  static defaultProps = {
    isCanceling: false,
    isExporting: false,
    isCancelAvailable: false,
    isTransportPoint: false,
  };

  constructor(props) {
    super(props);

    this.state = {
      dataList: props.dataList,
      eecsGoRows: props.eecsGoRows,
    };
  }

  onAmountChange = (value, rowId, source) => {
    this.setState((prev) => {
      const dataList = prev.dataList || [];
      const eecsGoRows = prev.eecsGoRows || [];

      const parsed = parseInt(value, 10);
      const nextQuantity = Number.isNaN(parsed) ? 1 : parsed;

      let target = source;
      let index;

      if (target === 'dataList') {
        index = dataList.findIndex(
          (item) => item[accountTableFields.GO_SEARCH_RESPONSE_ROW_ID] === rowId
        );
      } else if (target === 'eecsGoRows') {
        index = eecsGoRows.findIndex(
          (item) => item[accountTableFields.EECS_GO_ID] === rowId
        );
      }

      if (index === -1) return prev;

      const updates = { [accountTableFields.QUANTITY]: nextQuantity };

      if (target === 'dataList') {
        const nextDataList = dataList.slice();
        nextDataList[index] = { ...nextDataList[index], ...updates };
        return { ...prev, dataList: nextDataList };
      } else {
        const nextEecsGoRows = eecsGoRows.slice();
        nextEecsGoRows[index] = { ...nextEecsGoRows[index], ...updates };
        return { ...prev, eecsGoRows: nextEecsGoRows };
      }
    });
  };

  onRemove = (rowId) => {
    const { t } = this.props;
    this.setState((prev) => {
      const dataList = prev.dataList || [];
      const eecsGoRows = prev.eecsGoRows || [];

      const totalBefore = dataList.length + eecsGoRows.length;
      if (totalBefore <= 1) {
        message.warning(t('warningOne'));
        return prev;
      }

      const isEecsGo = String(rowId).startsWith('eecs_go-');

      const nextDataList = isEecsGo
        ? dataList
        : dataList.filter((item) => {
            return item.goSearchResponseRowId !== rowId;
          });

      const nextEecsGoRows = isEecsGo
        ? eecsGoRows.filter((item) => {
            const normalizedId = `eecs_go-${item.id}`;
            return normalizedId !== rowId;
          })
        : eecsGoRows;

      return {
        ...prev,
        dataList: nextDataList,
        eecsGoRows: nextEecsGoRows,
      };
    });
  };

  onGosPriceChange = (
    rawValue,
    rowId,
    source,
    { normalizeOnBlur = false } = {}
  ) => {
    this.setState((prev) => {
      const dataList = prev.dataList || [];
      const eecsGoRows = prev.eecsGoRows || [];

      let nextValue = null;
      if (rawValue !== null && rawValue !== undefined && rawValue !== '') {
        const parsed = parseFloat(rawValue);
        if (!Number.isNaN(parsed)) {
          nextValue = normalizeOnBlur ? Math.floor(parsed * 100) / 100 : parsed;
        }
      }

      let index;
      if (source === 'dataList') {
        index = dataList.findIndex(
          (item) => item[accountTableFields.GO_SEARCH_RESPONSE_ROW_ID] === rowId
        );
      } else {
        index = eecsGoRows.findIndex(
          (item) => item[accountTableFields.EECS_GO_ID] === rowId
        );
      }

      if (index === -1) return prev;

      const updates = { [accountTableFields.GOS_PRICE]: nextValue };

      if (source === 'dataList') {
        const nextDataList = dataList.slice();
        nextDataList[index] = { ...nextDataList[index], ...updates };

        return { ...prev, dataList: nextDataList };
      } else {
        const nextEecsGoRows = eecsGoRows.slice();
        nextEecsGoRows[index] = { ...nextEecsGoRows[index], ...updates };
        return { ...prev, eecsGoRows: nextEecsGoRows };
      }
    });
  };

  getColumns = () => {
    const {
      t,
      isUsersOtherAccount,
      isCanceling,
      isExporting,
      dataList,
      eecsGoRows,
    } = this.props;
    let isBiomethane =
      eecsGoRows?.length > 0 ||
      (dataList?.length > 0 &&
        dataList[0].gasType === gasType.BIOMETHANE_GAS_TYPE);
    let columns = [
      {
        title: t('table.productionUnitCode'),
        dataIndex: accountTableFields.PRODUCTION_DEVICE_CODE,
      },
      {
        title: t('table.productionTime'),
        dataIndex: accountTableFields.PRODUCTION_TIME,
      },
    ];

    if (!isExporting) {
      columns.push({
        title: t('table.expirationTime'),
        dataIndex: accountTableFields.EXPIRATION_TIME,
      });
    }

    if (isExporting) {
      columns.push({
        title: t('table.country'),
        dataIndex: 'country',
        render: (value) => translateCountryClassificator(value) || value,
      });
    }

    if (isBiomethane) {
      columns.push(
        {
          title: t('table.fuelType'),
          dataIndex: accountTableFields.FUEL_TYPE,
          render: translateFuelTypeClassificator,
        },
        {
          title: t('table.feedstock'),
          dataIndex: accountTableFields.FEEDSTOCK,
        },
        {
          title: t('table.biofuelType'),
          dataIndex: accountTableFields.BIOFUEL_TYPE,
          render: translateBiofuelTypeClassificator,
        },
        {
          title: t('table.productionPathway'),
          dataIndex: accountTableFields.PRODUCTION_PATHWAY,
          render: translateProductionPathwayClassificator,
        },
        {
          title: t('table.technology'),
          dataIndex: accountTableFields.TECHNOLOGY,
        },
        {
          title: t('table.sustainableBiofuel'),
          dataIndex: accountTableFields.SUSTAINABLE_BIOFUEL,
          render: (value) =>
            value === undefined || value === null
              ? '-'
              : translateBooleanClassificator(String(!!value)),
        }
      );
    }

    if (!isBiomethane) {
      columns.push(
        {
          title: t('table.hydrogenFuelType'),
          dataIndex: accountTableFields.HYDROGEN_FUEL_TYPE,
          render: translateHydrogenFuelTypeClassificator,
        },
        {
          title: t('table.hydrogenTechnologyType'),
          dataIndex: accountTableFields.HYDROGEN_TECHNOLOGY,
          render: translateHydrogenTechnologyTypeClassificator,
        }
      );
    }

    columns.push({
      title: t('table.quantity'),
      dataIndex: accountTableFields.QUANTITY,
      render: (value, record) => {
        const rowId =
          record.__source === 'dataList'
            ? record[accountTableFields.GO_SEARCH_RESPONSE_ROW_ID]
            : record[accountTableFields.EECS_GO_ID];

        const source =
          record.__source === 'dataList' ? 'dataList' : 'eecsGoRows';

        const isDisabledForTransport =
          this.props.isTransportPoint &&
          !record[accountTableFields.SUSTAINABLE_BIOFUEL];

        return (
          <InputNumber
            min={isDisabledForTransport ? 0 : 1}
            max={
              isDisabledForTransport
                ? 0
                : record[accountTableFields.MAX_QUANTITY]
            }
            value={isDisabledForTransport ? 0 : value}
            disabled={isDisabledForTransport}
            onChange={(numberValue) =>
              this.onAmountChange(numberValue, rowId, source)
            }
          />
        );
      },
    });

    if (isExporting) {
      columns.push({
        title: t('table.chgIntensity'),
        dataIndex: 'chgIntensity',
      });
    }

    columns.push({
      title: t('table.gosPrice'),
      dataIndex: accountTableFields.GOS_PRICE,
      render: (value, record) => {
        const rowId =
          record.__source === 'dataList'
            ? record[accountTableFields.GO_SEARCH_RESPONSE_ROW_ID]
            : record[accountTableFields.EECS_GO_ID];

        const source =
          record.__source === 'dataList' ? 'dataList' : 'eecsGoRows';

        const handleChange = (newValue) =>
          this.onGosPriceChange(newValue, rowId, source);

        const handleBlur = (event) =>
          this.onGosPriceChange(event?.target?.value, rowId, source, {
            normalizeOnBlur: true, // floor to 2 decimals on blur, same as your current logic
          });

        return (
          <InputNumber
            min={0}
            step={0.01}
            // If you want it hidden/null when it's someone else's account:
            value={isUsersOtherAccount ? null : value}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isUsersOtherAccount}
          />
        );
      },
    });

    columns.push({
      title: t('table.actions'),
      dataIndex: accountTableFields.GO_SEARCH_RESPONSE_ROW_ID,
      render: (value) => (
        <TableRowActions>
          <Button
            shape="circle"
            icon={<DeleteOutlined />}
            size="small"
            type="primary"
            onClick={() => this.onRemove(value)}
          />
        </TableRowActions>
      ),
    });

    if (isCanceling || isExporting) {
      columns = columns.filter(
        (col) => col.dataIndex !== accountTableFields.GOS_PRICE
      );
    }
    return columns;
  };

  handlePerformClick = async () => {
    const {
      t,
      performAction,
      isCanceling,
      isExporting,
      goAmountTotal,
      validateAction,
      isUsersOtherAccount,
      isTransportPoint,
    } = this.props;
    const { certificatesAmount, dataList, eecsGoRows } = this.state;

    const submitDataList = isTransportPoint
      ? dataList.filter((row) => row[accountTableFields.SUSTAINABLE_BIOFUEL])
      : dataList;

    if (this.isAmountInvalid(goAmountTotal, certificatesAmount)) {
      return message.error(t('errorAmount'));
    }

    if (validateAction?.(dataList, isUsersOtherAccount)) {
      return;
    }

    const { result, hasError } = await this.tryPerformAction(() =>
      performAction(submitDataList, eecsGoRows)
    );

    if (result?.error?.rowErrors) {
      return this.setState({ rowErrors: result.error.rowErrors });
    }

    this.handleOutcome({ result, hasError, t, isCanceling, isExporting });
  };

  isAmountInvalid = (total, amount) => !_.isNil(total) && total < amount;

  tryPerformAction = async (action) => {
    try {
      const result = await action();
      return { result, hasError: false };
    } catch {
      return { result: null, hasError: true };
    }
  };

  handleOutcome = ({ result, hasError, t, isCanceling, isExporting }) => {
    const isSuccess = !(result instanceof Error) && !hasError && !result?.error;

    const getMessageKey = (isSuccess, isCanceling, isExporting) => {
      if (isSuccess) {
        if (isCanceling) return 'successPerformCancel';
        if (isExporting) return 'successPerformExport';
        return 'successPerform';
      }

      if (isCanceling) return 'failurePerformCancel';
      if (isExporting) return 'failurePerformExport';
      return 'failurePerform';
    };

    const messageKey = getMessageKey(isSuccess, isCanceling, isExporting);

    if (isSuccess) {
      message.success(t(messageKey));
      return goBack();
    }

    notification.warning({ duration: 0, message: t(messageKey) });
  };

  render() {
    const { dataList, eecsGoRows, rowErrors = {} } = this.state;
    const { t, isCanceling, isCancelAvailable, isExporting } = this.props;
    const formatWithDayjs = (s) =>
      typeof s === 'string' && dayjs(s, 'YYYY-MM-DD', true).isValid()
        ? dayjs(s, 'YYYY-MM-DD').format('DD.MM.YYYY')
        : s;

    const normalizeRow = (row, source) => {
      if (source === 'dataList') {
        return {
          ...row,
          __source: 'dataList',
          productionDeviceCode: row.eicwCode,
          country: 'EE',
          feedstock:
            classifiersStore.getClassifierByKey(
              `FUEL_TYPE.${row.fuelType}.FEEDSTOCK.${row.feedstock}`
            ) +
            ' (' +
            row.feedstockAibCode +
            ')',
          technologyType: translateCleaningTechnologyClassificator(
            row.technologyType
          ),
        };
      }

      return {
        ...row,
        __source: 'eecsGoRows',
        goSearchResponseRowId: `eecs_go-${row.id}`,
        productionDeviceCode: row.deviceEecsCode,
        issueDate: row.productionPeriod,
        expirationDate: formatWithDayjs(row.dateOfExpiration),
        country: row.countryOfIssue,
        fuelType: translateFuelTypeClassificator(row.fuelTypeClassifier),
        feedstock:
          classifiersStore.getClassifierByKey(
            `FUEL_TYPE.${row.fuelTypeClassifier}.FEEDSTOCK.${row.feedstockClassifier}`
          ) +
          ' (' +
          row.energySource +
          ')',
        technologyType:
          translateProductionTechnologyClassificator(row.productionTechnology) +
          ' (' +
          row.productionTechnology +
          ')',
      };
    };

    const buildCombinedDataSource = (dataList, eecsGoRows) => {
      const left = dataList.map((row) => normalizeRow(row, 'dataList'));
      const right = eecsGoRows.map((row) => normalizeRow(row, 'eecsGoRows'));
      return [...left, ...right];
    };

    const combinedDataSource = buildCombinedDataSource(dataList, eecsGoRows);

    const hasNonSustainableForTransport =
      this.props.isTransportPoint &&
      combinedDataSource.some(
        (row) => !row[accountTableFields.SUSTAINABLE_BIOFUEL]
      );

    const certificatesAmount = combinedDataSource.reduce((sum, row) => {
      const isDisabledForTransport =
        this.props.isTransportPoint &&
        !row[accountTableFields.SUSTAINABLE_BIOFUEL];
      const q = isDisabledForTransport ? 0 : (row?.quantity ?? 0);
      return sum + q;
    }, 0);

    return (
      <>
        {hasNonSustainableForTransport && (
          <Alert
            type="warning"
            className="global-margin-bottom-10"
            message={tCommon('backendError.nonSustainableGoCancelTransport')}
          />
        )}
        <Table
          rowKey={accountTableFields.GO_SEARCH_RESPONSE_ROW_ID}
          rowClassName={(row) =>
            rowErrors[row.goSearchResponseRowId] ? 'row-error' : ''
          }
          dataSource={combinedDataSource}
          columns={this.getColumns()}
          pagination={false}
          virtual
          scroll={{ x: true, y: 600 }}
          expandable={{
            expandedRowRender: (record) => (
              <ExpandedRowRenderer
                record={record}
                rowErrors={rowErrors}
                t={t}
              />
            ),
            rowExpandable: (record) =>
              !!rowErrors[record.goSearchResponseRowId],
            expandedRowKeys: Object.keys(rowErrors),
            expandIconColumnIndex: -1,
          }}
          footer={() => (
            <div className="global-align-right">
              {t('footer', { certificates: certificatesAmount })}
            </div>
          )}
        />
        <TableActions>
          <Button onClick={goBack}>{t('buttonBack')}</Button>
          <Popconfirm
            title={
              isCanceling
                ? t('popconfirmTitleCancel')
                : isExporting
                  ? t('popconfirmTitleExport')
                  : t('popconfirmTitle')
            }
            onConfirm={this.handlePerformClick}
            okText={tCommon('popconfirm.buttonYes')}
            cancelText={tCommon('popconfirm.buttonNo')}
          >
            {(!isCanceling || isCancelAvailable) && (
              <Button type="primary" className="global-margin-left-10">
                {t('buttonPerform')}
              </Button>
            )}
          </Popconfirm>
        </TableActions>
      </>
    );
  }
}

export default withTranslation(NAMESPACES.transferringCertificates)(
  PerformCertificatesTable
);
